import {streamify, tracer} from '@s-ui/decorators'
import {UseCase} from '@s-ui/domain'

import {FiltersSearchValueObject} from '../../search/ValueObjects/FiltersSearchValueObject.js'

const EMPTY_OBJECT = {}
const EMPTY_FROM_TO_OBJECT = {from: null, to: null}
/**
 * @implements UseCase
 */
@streamify('execute')
class CreateAlertUserAlertUseCase extends UseCase {
  /**
   * @constructor
   * @param {Object} deps
   * @param {GetSearchParamsRequestService} deps.getSearchParamsRequestService
   * @param {HTTPAdsRepository} deps.repository
   */
  constructor({
    config,
    searchRequestValueObjectFactory,
    getCurrentUserService,
    getSearchParamsRequestService,
    repository,
    searchHasNoFiltersUserAlertError,
    userNotLoggedInUserErrorFactory
  }) {
    super({
      config,
      getCurrentUserService,
      getSearchParamsRequestService,
      repository,
      searchHasNoFiltersUserAlertError,
      userNotLoggedInUserErrorFactory
    })
    this._config = config
    this._defaultSortOrder = this._config.get('ORDER').DESC
    this._defaultSortBy = this._config.get('SORT_FIELDS').RELEVANCE
    this._defaultFilters = this._config.get('search').DEFAULT_FILTERS
    this._unselectedOfferType = this._config.get('search').UNSELECTED_OFFER_TYPE
    this._getCurrentUserService = getCurrentUserService
    this._searchRequestValueObjectFactory = searchRequestValueObjectFactory
    this._getSearchParamsRequestService = getSearchParamsRequestService
    this._repository = repository
    this._searchHasNoFiltersUserAlertError = searchHasNoFiltersUserAlertError
    this._userNotLoggedInUserErrorFactory = userNotLoggedInUserErrorFactory
  }

  /**
   * @param {Object} params
   * @param {Object} data.search
   * @return {Promise<Object, Error>} alert entity object
   */
  @tracer({metric: 'CreateAlertUserAlertUseCase#execute'})
  async execute(
    {
      adId,
      listingSectionId,
      search,
      search: {
        page = 1,
        sortBy = this._defaultSortBy,
        sortOrder = this._defaultSortOrder,
        categoryId,
        offerTypeId,
        filteredOfferTypeId,
        ...filters
      }
    } = {
      search: {
        page: 1,
        sortBy: this._defaultSortBy,
        sortOrder: this._defaultSortOrder
      }
    }
  ) {
    const user = await this._getCurrentUserService.execute()
    if (!this._hasFilters(filters)) {
      return Promise.reject(
        this._searchHasNoFiltersUserAlertError({
          message: 'To create an alert the search must have filters'
        })
      )
    }

    if (!user) {
      return Promise.reject(
        this._userNotLoggedInUserErrorFactory({
          message: 'To create an alert you must be logged in'
        }).toJSON()
      )
    }

    const userId = user.getId()

    const filtersSearchValueObject = FiltersSearchValueObject.createFromFilters({
      config: this._config,
      filters: search,
      listingSectionId
    })

    const locationApi = filtersSearchValueObject.locationApi

    const [error, response] = await this._repository.createAlert({
      adId,
      filters: {...filtersSearchValueObject.getSearchFromFilters(), ...(locationApi && {location: locationApi})},
      userId
    })

    if (error) return Promise.reject(error)
    return response.toJSON()
  }

  // To consider a filter included in the search object, it must be different than
  // null, undefined, empty array, empty object, and be different than the default value.
  //
  // For example: model: [],
  // It would be considered there's no filter
  _hasFilters = filters => {
    const filterNames = Object.keys(filters)
    // values can be a number, string, array and objects.
    // We make sure to remove all of those values that are [], {}, undefined, null, 0.
    const filterNamesWithValues = filterNames.filter(name => {
      const filterValue = filters[name]
      if (this._isValidFilterValue(filterValue)) {
        return name
      }
    })

    let hasAnyFilter = false
    filterNamesWithValues.forEach(name => {
      // The early return is to optimize the loop and stop iterating in case
      // we found a filter in the search object
      if (hasAnyFilter) {
        return
      }

      // stringify allows us to compare different structures in the same loop
      // values can be objects, arrays, strings or numbers.
      const filterValue = JSON.stringify(filters[name])
      const defaultValue = JSON.stringify(this._defaultFilters[name])
      hasAnyFilter = filterValue !== defaultValue
    })

    return hasAnyFilter
  }

  _isValidFilterValue = filterValue =>
    !(
      !filterValue ||
      this._isEmptyArray(filterValue) ||
      this._isEmptyObject(filterValue) ||
      this._isEmptyFromToFormatObject(filterValue)
    )

  _isEmptyArray = array => Array.isArray(array) && (array.length === 0 || array[0] === 0)

  _isEmptyObject = value => JSON.stringify(value) === JSON.stringify(EMPTY_OBJECT)

  _isEmptyFromToFormatObject = value => JSON.stringify(value) === JSON.stringify(EMPTY_FROM_TO_OBJECT)
}

export default CreateAlertUserAlertUseCase
